import {ElementRef, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {BehaviorSubject, Observable, of, tap} from 'rxjs';
import {SwissFieldBase} from '../swiss-forms/fields/swiss-field-base';
import {ObjektModels} from '../models/objekt-models';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {HelperService} from './helper.service';
import {MainDataModels} from '../models/maindata-models';
import {LocalStorageService} from '../local-storage.service';
import {AdresseModels} from '../models/adresse-models';
import {v4 as uuidv4} from 'uuid';
import {SwissSettingsService} from './swiss-settings.service';
import {MatDialog} from '@angular/material/dialog';
import {ObjektDialogComponent} from 'src/app/swiss-forms/objekt-dialog/objekt-dialog.component';
import {MessageBoxService} from './message-box.service';
import {SwissInternModels} from '../models/swissintern-models';
import {ObjektSchutzComponent} from '../home-layout/objekte/objekt-schutz/objekt-schutz.component';
import {GlobalModels} from '../models/global-models';
import {PDFReportService} from './pdfreport.service';
import {ReportModels} from '../models/report-models';
import {ServiceAppModels} from '../models/serviceapp-models';
import {DokumentModels} from '../models/dokument-models';
import {UserAuthenticationService} from './user-authentication.service';
import {TreeviewService} from './treeview.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError} from 'rxjs/operators';
import BaseFile = SwissInternModels.BaseFile;

export interface TreeViewItemsResponseObject<T> extends ObjectData {
    id: T;
    pid?: T;
    hasChild: boolean;
    // there is a typo in IntraService (｡◕‿‿◕｡)
    expandend: boolean;
    name?: string;
    color?: string;
}

export interface ObjectData {
    objectID?: string;
    objectNr?: string;
    note4?: string,
    imageURL?: string;
    description?: string;
    projectLeader?: string;
    status?: string;
    town?: string;
    name?: string;
}

export interface TreeViewItemSmall {
    objectID: string;
    parentObject?: string;
    name: string;
    hasChild: boolean;
    expandend: boolean;
    imageURL?: string;
}

export interface ObjectRangeOption {
    id?: string;
    description?: string;
}

@Injectable({
    providedIn: 'root',
})
export class ObjektDataService {
    private webServiceConfig?: SwissAppConfigModels.WebServiceConfig;
    private swissAppConfig?: SwissAppConfigModels.SwissAppConfig;
    private swissAppReports?: SwissAppConfigModels.SwissAppReport[];
    private choosedObject = new BehaviorSubject(ObjektModels.EmptyObjektElement());
    private currentObjectID: string = '';

    private objViewConfig: number = 1;

    /* Offline-Daten */
    private storageWebMain: string = 'SwissWebMainDB';
    private storageWebConfig: string = 'SwissWebConfigDB';
    private dbStorageSI: string = 'SwissInternDB';
    private dbAdressTypen: AdresseModels.AddressTypes[] = [];
    private dbObjekte: ObjektModels.ObjektElement[] = [];
    private dbObjektTypes: ObjektModels.ObjektType[] = [];
    private dbObjektAdressZuord: ObjektModels.ObjektAdresseZuord[] = [];
    private dbObjektAdressen: ObjektModels.ObjektAdresseShort[] = [];
    private dbObjektBezeichnungen: MainDataModels.SearchFieldResponseObjekt[] = [];
    private dbObjektOfflineStates: ObjektModels.ObjektOfflineStatus[] = [];
    private dbRapporte: SwissInternModels.BaseFile[] = [];
    private doLoadRapportHeaderLogo: boolean = false;
    private rapportLogoLoaded: boolean = false;
    private logoBlob?: Blob;
    private logoBase64Str: string = '';
    private currObjektReportTemplate: DokumentModels.SwissAppFileReport | undefined = undefined;
    private dbObjektReporte: SwissInternModels.BaseFile[] = [];
    private objReportUploadElement?: SwissInternModels.ObjektReportUploadElement;
    /* Ende Offline-Daten */

    private objektSuche: ObjektModels.ObjektSucheDaten = {
        objektStatus: [], objektOrte: [], projektleiter: []
    };
    private objektFilter?: ObjektModels.ObjektFilter;
    private reloadObjActivitiesFunc?: () => void;
    private loadSingleObjectByObjectID?: () => void;
    private loadAddAdressInObjectDialogFunc?: (oaz: ObjektModels.ObjektAdresseZuord) => void;

    constructor(private http: HttpClient, private userAuthService: UserAuthenticationService, private svcSwissSettings: SwissSettingsService, private helperService: HelperService, private localStorageService: LocalStorageService, public dialog: MatDialog, private svcPDFReport: PDFReportService, private svcMessageBox: MessageBoxService, private treeViewCache: TreeviewService) {
        this.helperService.getSwissWebServiceConfig$().pipe(
            takeUntilDestroyed(),
            tap((config) => {
                this.webServiceConfig = config;
            })
        ).subscribe();
        // Sobald die Settings geladen sind, wird die Methode loadSwissAppConfigs aufgerufen
        this.svcSwissSettings.registerSwissAppSettings(this.loadSwissAppConfigs.bind(this));

        // Sobald die Reporte fertig geladen sind, wird die Methode loadObjektReportTemplate aufgerufen
        this.svcSwissSettings.registerSwissAppReports(this.loadObjektReportTemplate.bind(this));

        this.helperService.getOnlineStateChanged().pipe(takeUntilDestroyed()).subscribe(async (isOnline) => {
            if (isOnline) {
                if (this.treeViewCache.treeData) {
                    this.treeViewCache.treeData$.next(this.treeViewCache.treeData!);
                }
            } else {
                const res = await this.getOfflineObjektBaum();
                this.treeViewCache.treeData$.next(await TreeviewService.createTree(res));
            }
        });
    }

    /* Objekt-Aktivitäten neu laden */
    registerObjActivitiesFunc(fn: () => void) {
        this.reloadObjActivitiesFunc = fn;
    }

    registerLoadSingleObject(fn: () => void) {
        this.loadSingleObjectByObjectID = fn;
    }

    /* Im Objekt-Erstellen Dialog die ausgewählte Adresse/Kontakt laden */
    registerAddAdressInObjectDialogFunc(fn: (oaz: ObjektModels.ObjektAdresseZuord) => void) {
        this.loadAddAdressInObjectDialogFunc = fn;
    }

    public async callAddAdressInObjectDialog(oaz: ObjektModels.ObjektAdresseZuord) {
        if (this.loadAddAdressInObjectDialogFunc) {
            this.loadAddAdressInObjectDialogFunc(oaz);
        }
    }

    async loadAdressTypes() {
        this.getAddressTypen().subscribe({
            next: (res: AdresseModels.AddressTypes[]) => {
                this.dbAdressTypen = res;
            }, error: () => {
                this.localStorageService.get('adressTypen', this.storageWebMain).then((value) => {
                    if (value !== false) {
                        this.dbAdressTypen = <AdresseModels.AddressTypes[]>value;
                    }
                });
            }
        });
    }

    async loadSwissAppConfigs() {
        this.swissAppConfig = this.svcSwissSettings.getSwissAppConfig();
        this.objViewConfig = this.svcSwissSettings.getObjViewConfig();
        await this.loadAdressTypes();
        if (this.doLoadRapportHeaderLogo) {
            await this.loadObjektRapportLogo();
        }
    }

    public loadObjektReportTemplate() {
        this.currObjektReportTemplate = this.svcSwissSettings.getObjektReportFile();
        this.loadObjektReporte();
    }

    public getObjektSuche() {
        return this.objektSuche;
    }

    getSwissAppConfig() {
        return this.swissAppConfig;
    }

    setCurrentObject(myObject: ObjektModels.ObjektElement | string, noTrigger = false) {
        if (typeof myObject !== 'string') { // myobject darf nicht 'yes' drin stehen haben.
            this.currentObjectID = myObject.objectID;
            if (!noTrigger) {
                this.choosedObject.next(myObject);
            }
        }
    }

    getCurrentObject() {
        return this.choosedObject.getValue();
    }

    setCurrentObjectID(objectID: string) {
        this.currentObjectID = objectID;
        if (this.loadSingleObjectByObjectID) {
            this.loadSingleObjectByObjectID();
        }
    }

    getCurrentObjectID() {
        return this.currentObjectID;
    }

    getObjektByID(objectID: string, updateLastUsed = true): Observable<ObjektModels.ObjektElement> {
        let url = this.webServiceConfig!.objUrl;
        return this.http.get<ObjektModels.ObjektElement>(url + '/byObjectID?ObjectID=' + objectID + '&UpdateLastUsed=' + updateLastUsed, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getOfflineObjektByID(objectID: string): Observable<ObjektModels.ObjektElement> {
        if (this.dbObjekte.length > 0) {
            return of(this.dbObjekte.filter((o) => o.objectID == objectID)[0]);
        }
        return of();
    }

    getSubObjektByID(objectID: string): Observable<ObjektModels.ObjektElement[]> {
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        let url = this.webServiceConfig!.objUrl;
        return this.http.get<ObjektModels.ObjektElement[]>(url + '/SubObjekte?EmployeeID=' + user.employeeID + '&Mandator=' + user.mandatorID + '&ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getAddressesByObjektID(objectID: string): Observable<ObjektModels.ObjektAdresse[]> {
        let url = this.webServiceConfig!.objUrl;
        return this.http.get<ObjektModels.ObjektAdresse[]>(url + '/GetAddressesByObjectID?ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getObjektAdressenZuordnungen(objectID: string): Observable<ObjektModels.ObjektAdresseZuord[]> {
        let url = this.webServiceConfig!.objUrl;
        return this.http.get<ObjektModels.ObjektAdresseZuord[]>(url + '/ObjektAdressZuordnungenOhneUnterObjekte?ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getOfflineObjektAdressenZuordnungen(objectID: string): Observable<ObjektModels.ObjektAdresseZuord[]> {
        let url = this.webServiceConfig!.objUrl;
        return this.http.get<ObjektModels.ObjektAdresseZuord[]>(url + '/ObjektAdressZuordnungen?ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getOfflineObjektAdressen(objectID: string): Observable<ObjektModels.ObjektAdresseShort[]> {
        let url = this.webServiceConfig!.objUrl;
        return this.http.get<ObjektModels.ObjektAdresseShort[]>(url + '/ObjektAdressen?ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getOfflineAddressesByObjektID(objectID: string): Promise<ObjektModels.ObjektAdresse[]> {
        let currObjektAdressen: ObjektModels.ObjektAdresse[] = [];
        return new Promise((resolve) => {
            // Adresszuordnungen laden
            this.localStorageService
                .get('offlineObjekteAdressZuord', this.storageWebMain)
                .then((value) => {
                    if (value !== false) {
                        this.dbObjektAdressZuord = <ObjektModels.ObjektAdresseZuord[]>value;
                        if (this.dbObjektAdressZuord.length > 0) {
                            let currObjAdrZuord: ObjektModels.ObjektAdresseZuord[] = this.dbObjektAdressZuord.filter((oaz) => oaz.objectID == objectID);
                            // Sind Offline-ObjektAdressZuordnungen vorhanden?
                            if (currObjAdrZuord.length > 0) {
                                // Adressen laden
                                this.localStorageService
                                    .get('offlineObjekteAdressen', this.storageWebMain)
                                    .then((value) => {
                                        if (value !== false) {
                                            this.dbObjektAdressen = <ObjektModels.ObjektAdresseShort[]>value;
                                            if (this.dbObjektAdressen.length > 0) {
                                                currObjAdrZuord.forEach((objAdrZuord) => {
                                                    let adr = this.dbObjektAdressen.filter((oa) => oa.adressID == objAdrZuord.adressID)[0];
                                                    let contactAdr: any;
                                                    if (objAdrZuord.contactID) {
                                                        contactAdr = this.dbObjektAdressen.filter((oa) => oa.adressID == objAdrZuord.contactID)[0];
                                                    }

                                                    let currObjAdresse: ObjektModels.ObjektAdresse = {
                                                        id: objAdrZuord.id,
                                                        addressType: objAdrZuord.addressType,
                                                        addressTypeDescription: this.dbAdressTypen.filter((at) => at.addressTypeNumber == objAdrZuord.addressType)[0].description,
                                                        adressID: objAdrZuord.adressID,
                                                        firstName: adr.firstName,
                                                        lastName: adr.lastName,
                                                        fullName: adr.fullName,
                                                        street1: adr.street1,
                                                        zip: adr.zip,
                                                        city: adr.city,
                                                        phone01: adr.phone01,
                                                        contactID: objAdrZuord.contactID,
                                                        attentionOf: undefined,
                                                        email: undefined,
                                                        mobile: undefined,
                                                    };

                                                    if (contactAdr != undefined) {
                                                        currObjAdresse.attentionOf = contactAdr.attentionOf;
                                                        currObjAdresse.email = contactAdr.email;
                                                        currObjAdresse.mobile = contactAdr.mobile;
                                                    }
                                                    currObjektAdressen.push(currObjAdresse);
                                                });
                                                resolve(currObjektAdressen);
                                            }
                                        } else {
                                            resolve(currObjektAdressen);
                                        }
                                    });
                            } else {
                                resolve(currObjektAdressen);
                            }
                        } else {
                            resolve(currObjektAdressen);
                        }
                    } else {
                        resolve(currObjektAdressen);
                    }
                });
        });
    }

    getClientAddress(objectID: string, adressTyp: GlobalModels.AdressTypes): Promise<ObjektModels.ObjektAdresseShort | undefined> {
        return new Promise((resolve) => {
            this.getOfflineObjektAdressenZuordnungen(objectID).subscribe({
                next: (res) => {
                    let objekteAdrZuord: ObjektModels.ObjektAdresseZuord[] = res;
                    if (objekteAdrZuord.length > 0) {
                        let clientAdrZuord = objekteAdrZuord.filter(oaz => oaz.addressType === adressTyp && oaz.objectID === objectID);
                        if (clientAdrZuord && clientAdrZuord.length > 0) {
                            this.getOfflineObjektAdressen(objectID).subscribe({
                                next: (res) => {
                                    let objekteAdressen: ObjektModels.ObjektAdresseShort[] = res;
                                    if (objekteAdressen.length > 0) {
                                        let objektadresse = objekteAdressen.filter(x => x.adressID === clientAdrZuord[0].adressID)[0];
                                        resolve(objektadresse);
                                    } else {
                                        resolve(undefined);
                                    }
                                }
                            });
                        } else {
                            // Keine Kundenadresse vorhanden
                            resolve(undefined);
                        }
                    } else {
                        resolve(undefined);
                    }
                }
            });
        });
    }

    getObjectNumber(objectType: string, params?: { parentObjectID?: string, objectNumberRangeID?: string }): Observable<string> {
        if (!this.webServiceConfig) {
            throw new Error('WebServiceConfig is not set');
        }
        let url = this.webServiceConfig.objUrl;

        url += '/GetObjektNummerByObject?ObjectType=' + Number(objectType);

        if (params?.objectNumberRangeID) {
            url += '&ObjectNumberRangeID=' + params.objectNumberRangeID;
        }

        if (params?.parentObjectID) {
            url += '&ObjectID=' + params.parentObjectID;
        }

        return this.http.get<string>(url, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getObjectNumberIsFree(objectNumber: string): Observable<boolean> {
        let url = this.webServiceConfig!.objUrl;
        return this.http.get<boolean>(url + '/GetObjektNummerIsFree?ObjectNumber=' + objectNumber, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getObjectView(objViewConfig: number): Observable<ObjektModels.ObjectListViewItem[]> {
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        let url = this.webServiceConfig!.objUrl;
        let urlEnd = '/GetObjektView?EmployeeID=' + user.employeeID + '&Mandator=' + user.mandatorID + '&ObjViewConfig=' + this.svcSwissSettings.getObjViewConfig();

        if (this.svcSwissSettings.getObjViewConfig() == 5) {
            urlEnd = '/GetObjektView2?' + 'EmployeeID=' + user.employeeID + '&Mandator=' + user.mandatorID + '&ObjViewConfig=' + 2;
        }

        if (this.svcSwissSettings.getObjViewConfig() == 6) {
            urlEnd = '/GetObjektView2?' + 'EmployeeID=' + user.employeeID + '&Mandator=' + user.mandatorID + '&ObjViewConfig=' + 3;
        }

        if (objViewConfig == 4) { // Bei der erweiterten Objektsuche darf nur ermittelt werden, wenn Kriterien vorhanden sind.
            if (this.objektFilter) {
                if (this.objektFilter.objektstatus != '' || this.objektFilter.projektleiter != '' || this.objektFilter.objektorte != '' || this.objektFilter.note4 != '') {
                    if (this.objektFilter.objektstatus != '') {
                        urlEnd += '&ObjektStatus=' + this.objektFilter.objektstatus;
                    }
                    if (this.objektFilter.projektleiter != '') {
                        urlEnd += '&ProjektLeiter=' + this.objektFilter!.projektleiter;
                    }
                    if (this.objektFilter?.objektorte != '') {
                        urlEnd += '&ObjektOrte=' + this.objektFilter!.objektorte;
                    }
                    if (this.objektFilter?.note4 != '') {
                        urlEnd += '&Note4=' + this.objektFilter!.note4;
                    }
                } else {
                    urlEnd += '&ObjektStatus=-1';
                }  // Ermittelt ein leeres Array, da keine Kriterien vorhanden sind.
            } else {
                urlEnd += '&ObjektStatus=-1';
            }  // Ermittelt ein leeres Array, da keine Kriterien vorhanden sind.
        }

        return this.http.get<ObjektModels.ObjectListViewItem[]>(url + urlEnd, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    /* START OBJEKTTYPEN */
    loadObjektTypes() {
        return new Promise((resolve) => {
            this.getObjektTypes().then(res => {
                if (res) {
                    this.dbObjektTypes = res;
                    this.saveObjektTypes();
                    resolve(true);
                }
            });
        });
    }

    getObjektTypes(): Promise<ObjektModels.ObjektType[]> {
        let url = this.webServiceConfig!.objUrl;
        let urlEnd = '/GetObjektTypes';
        return new Promise(resolve => {
            this.http.get<ObjektModels.ObjektType[]>(url + urlEnd, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).subscribe({
                next: (res) => {
                    resolve(res);
                }, error: async () => {
                    resolve(await this.localStorageService.get('offlineObjektTypen', this.storageWebMain, false, []));
                }
            });
        });
    }

    getObjektTypeImageURL(typeNumber: Number) {
        if (this.dbObjektTypes.length > 0) {
            let objektType = this.dbObjektTypes.filter((ot) => ot.typeNumber === typeNumber)[0];
            if (objektType) {
                return './assets/tv_icons/' + objektType.imagePath;
            } else {
                return '';
            }
        } else {
            return '';
        }
    }

    async saveObjektTypes() {
        await this.localStorageService.add('offlineObjektTypen', this.dbObjektTypes, this.storageWebMain);
    }

    getObjektBaumBottomUp(objectID: string): Observable<TreeViewItemSmall> {
        let url = this.webServiceConfig!.objUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        return this.http.get<TreeViewItemSmall>(url + '/GetObjektTreeBottomUp?EmployeeID=' + user.employeeID + '&Mandator=' + user.mandatorID + '&ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getObjektBaumTopDown(objectID: string): Observable<TreeViewItemSmall> {
        let url = this.webServiceConfig!.objUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        return this.http.get<TreeViewItemSmall>(url + '/GetObjektTreeTopDown?EmployeeID=' + user.employeeID + '&Mandator=' + user.mandatorID + '&ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    async getOfflineObjektBaum(): Promise<TreeViewItemsResponseObject<any>[]> {
        const offlineTree = await this.localStorageService.get('offlineObjektBaum', this.storageWebMain, false, []);
        this.dbObjekte = await this.localStorageService.get('offlineObjekte', this.storageWebMain, false, []);
        return offlineTree.filter(
            (x) => x.objectID === null || this.dbObjekte.filter((o) => o.objectID === x.objectID)[0] != null
        );
    }

    getSuchfeldBeschreibungen(): Observable<MainDataModels.SearchFieldResponseObjekt[]> {
        let url = this.webServiceConfig!.objUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        if (this.objViewConfig == 5) {
            let urlEnd = '/GetSearchDesc?Mandator=' + user.mandatorID + '&EmployeeID=' + user.employeeID + '&ObjViewConfig=' + 2;
            return this.http.get<MainDataModels.SearchFieldResponseObjekt[]>(url + urlEnd, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).pipe(
                tap(data => {
                    this.localStorageService.add('offlineSearchDesc', data, 'SwissWebMainDB', false, true);
                }),
                catchError(async (err, caught) => {
                    return await this.localStorageService.get('offlineSearchDesc', 'SwissWebMainDB', true, []);
                })
            );
        } else {
            if (this.objektFilter) {
                return this.getSuchfeldBeschreibungenFromFilter();
            } else {
                let urlEnd = '/GetObjektTreeDesc?Mandator=' + user.mandatorID + '&UserID=' + user.userID + '&ObjViewConfig=' + this.objViewConfig;
                return this.http.get<MainDataModels.SearchFieldResponseObjekt[]>(url + urlEnd, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).pipe(
                    tap(data => {
                        this.localStorageService.add('offlineTreeDesc', data, 'SwissWebMainDB', false, true);
                    }),
                    catchError(async (err, caught) => {
                        return await this.localStorageService.get('offlineTreeDesc', 'SwissWebMainDB', true, []);
                    })
                );

            }
        }
    }

    getSuchfeldBeschreibungenFromFilter(): Observable<MainDataModels.SearchFieldResponseObjekt[]> {
        let url = this.webServiceConfig!.objUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        let urlEnd = '/GetObjektDesc?Mandator=' + user.mandatorID + '&UserID=' + user.userID;

        if (this.objektFilter?.objektstatus != '') {
            urlEnd += '&ObjektStatus=' + this.objektFilter!.objektstatus;
        }

        if (this.objektFilter?.projektleiter != '') {
            urlEnd += '&ProjektLeiter=' + this.objektFilter!.projektleiter;
        }

        if (this.objektFilter?.objektorte != '') {
            urlEnd += '&ObjektOrte=' + this.objektFilter!.objektorte;
        }

        if (this.objektFilter?.note4 != '') {
            urlEnd += '&Note4=' + this.objektFilter!.note4;
        }

        return this.http.get<MainDataModels.SearchFieldResponseObjekt[]>(url + urlEnd, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    async saveSuchfeldBeschreibungen(objektBezeichnungen: MainDataModels.SearchFieldResponseObjekt[]) {
        await this.localStorageService.add('offlineObjektBezeichnungen', objektBezeichnungen, this.storageWebMain);
    }

    getOfflineSuchfeldBeschreibungen(): Promise<MainDataModels.SearchFieldResponseObjekt[]> {
        return new Promise((resolve) => {
            this.localStorageService.get('offlineObjektBezeichnungen', this.storageWebMain).then((value) => {
                if (value !== false) {
                    this.dbObjektBezeichnungen = <MainDataModels.SearchFieldResponseObjekt[]>value;
                    this.dbObjektBezeichnungen = this.dbObjektBezeichnungen.filter((ob) => this.dbObjekte.filter((o) => o.objectID === ob.objectID)[0] != null);
                    resolve(this.dbObjektBezeichnungen);
                }
            });
        });
    }


    getObjectActivities(objectID: string): Observable<ObjektModels.ObjektAktivitaetListElement[]> {
        let url = this.webServiceConfig!.objUrl;
        return this.http.get<ObjektModels.ObjektAktivitaetListElement[]>(url + '/ObjektAktivitaeten?ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getFormFields(id_SwissAppGroup: number): Observable<SwissFieldBase<any>[]> {
        let url = this.webServiceConfig!.objUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        return this.http.get<SwissFieldBase<any>[]>(url + '/GetObjektFields?ID_SwissAppGroup=' + id_SwissAppGroup + '&Mandator=' + user.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getFormFieldsByObjType(id_SwissAppGroup: number, objectType: string, neuesObjekt: boolean = false): Observable<SwissFieldBase<any>[]> {
        let url = this.webServiceConfig!.objUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        let neuesObjektParam: number = 0;
        if (neuesObjekt) {
            neuesObjektParam = 1;
        }
        return this.http.get<SwissFieldBase<any>[]>(url + '/GetObjektFields?ID_SwissAppGroup=' + id_SwissAppGroup + '&Mandator=' + user.mandatorID + '&objectType=' + objectType + '&neuesObjekt=' + neuesObjektParam, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    public async getFormFieldsFromLocalStorageIfExists(id_SwissAppGroup: number, objectType: number, neuesObjekt: boolean = false): Promise<Observable<SwissFieldBase<any>[]>> {
        const field = await this.getOfflineFormFields(id_SwissAppGroup, objectType);

        if (field) {
            return of(field);
        } else {
            return this.getFormFieldsByObjType(id_SwissAppGroup, '' + objectType, neuesObjekt);
        }
    }

    public async setOfflineFormFields(id_SwissAppGroup: number, objectType: number | string, fields: SwissFieldBase<any>[]) {
        let config: Map<string, any> | any[] = await this.localStorageService.get('feldKonfigurationen', this.storageWebMain, true, new Map<string, any>());
        if (!(config instanceof Map)) {
            config = new Map<string, any>();
        }
        config.set(id_SwissAppGroup + '_' + objectType, fields);
        await this.localStorageService.add('feldKonfigurationen', config, this.storageWebMain, false, true);
    }

    async getOfflineFormFields(id_SwissAppGroup: number, objectDataType: number): Promise<SwissFieldBase<any>[] | null> {
        const value = await this.localStorageService.get('feldKonfigurationen', this.storageWebMain, true);
        if (value) {
            return value.get(id_SwissAppGroup + '_' + objectDataType);
        }
        return null;
    }

    getAllFormFields(): Observable<SwissFieldBase<any>[]> {
        let url = this.webServiceConfig!.objUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        return this.http.get<SwissFieldBase<any>[]>(url + '/GetAllObjektFields?&Mandator=' + user.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getAddressTypen(): Observable<AdresseModels.AddressTypes[]> {
        let url = this.webServiceConfig!.adrUrl;
        return this.http.get<AdresseModels.AddressTypes[]>(url + '/AdressTypen', this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    getLoadedAddressTypen() {
        return this.dbAdressTypen;
    }

    newObjekt(currObjekt: ObjektModels.ObjektElement): Observable<ObjektModels.ObjektElement> {
        const creator = this.userAuthService.getCurrentAuthenticatedUser()!;
        if (creator?.loginname) {
            currObjekt.createdBy = creator.loginname;
        }
        let url = this.webServiceConfig!.objUrl;
        return this.http.post<ObjektModels.ObjektElement>(url + '/NewObjekt', currObjekt, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    updateObjektField(currObjekt: ObjektModels.ObjektElement, fieldToUpdate: string): Observable<ObjektModels.ObjektElement | boolean> {
        if (!this.helperService.isWebServiceAvailable()) {
            return of(true);
        }

        const headers = new HttpHeaders({
            'Content-Type': 'application/json', Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        currObjekt.modifiedBy = user.loginname;

        const params: HttpParams = new HttpParams().set('updatedFields', fieldToUpdate);

        let url = this.webServiceConfig!.objUrl;
        return this.http.post<ObjektModels.ObjektElement>(url + '/UpdateObjekt', currObjekt, {
            headers: headers, params: params,
        });
    }

    updateObjekt(currObjekt: ObjektModels.ObjektElement, allUpdatedFields: SwissFieldBase<any>[]): Observable<ObjektModels.ObjektElement | boolean> {
        if (!this.helperService.isWebServiceAvailable()) {
            return of(true);
        }

        const headers = new HttpHeaders({'Content-Type': 'application/json', Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),});
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        currObjekt.modifiedBy = user.loginname;
        let updatedFields: string = '';
        if (allUpdatedFields.length > 0) {
            allUpdatedFields.forEach((field) => {
                updatedFields += field.options.dataBinding! + ', ';
            });
        }

        const params: HttpParams = new HttpParams().set('updatedFields', updatedFields);

        let url = this.webServiceConfig!.objUrl;
        return this.http.post<ObjektModels.ObjektElement>(url + '/UpdateObjekt', currObjekt, {
            headers: headers, params: params,
        });
    }

    deleteObjekt(currObjekt: ObjektModels.ObjektElement): Observable<ObjektModels.ObjektElement> {
        let url = this.webServiceConfig!.objUrl;
        return this.http.post<ObjektModels.ObjektElement>(url + '/DelObjekt', currObjekt, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    saveObjectInDB(objectID: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.getObjektByID(objectID).subscribe({
                next: async (res) => {
                    let currObjekt = res;

                    this.getFormFieldsByObjType(1, currObjekt.objectType).subscribe({
                        next: async (f1: any[]) => {
                            await this.setOfflineFormFields(1, currObjekt.objectType, f1);
                            this.getFormFieldsByObjType(2, currObjekt.objectType).subscribe({
                                next: async (f2: any[]) => {
                                    await this.setOfflineFormFields(2, currObjekt.objectType, f2);
                                }
                            });
                        }
                    });

                    this.dbObjekte = await this.localStorageService.get('offlineObjekte', this.storageWebMain, false, []);
                    const searchValues = this.treeViewCache.getObjectListData()?.find(obj => obj.objectID == currObjekt.objectID);
                    const obj = currObjekt;
                    obj['imageURL'] = obj['imageURL'] ?? searchValues?.objectTypeImg;
                    obj['projectLeader'] = searchValues?.projectLeader;
                    obj['name'] = searchValues?.name;
                    obj['objectTypeImg'] = searchValues?.objectTypeImg;
                    obj['objectTypeDesc'] = searchValues?.objectTypeDesc;
                    obj['depth'] = searchValues?.depth;
                    obj['parentID'] = obj['parentID'] ?? obj.parentObject;

                    let index = this.dbObjekte.findIndex((x) => x.objectID == objectID);
                    if (index > -1) {
                        this.dbObjekte[index] = obj;
                    } else {
                        this.dbObjekte.push(obj);
                    }

                    await this.localStorageService.add('offlineObjekte', this.dbObjekte, this.storageWebMain);

                    if (this.objViewConfig == 2 || this.objViewConfig == 5) {
                        resolve(true);
                    } else {
                        resolve(await this.saveParentObjectsInDB(currObjekt.parentObject));
                    }
                }, error: () => resolve(false),
            });
        });
    }

    async saveParentObjectsInDB(parentID: string | undefined): Promise<boolean> {
        return new Promise((resolve) => {
            if (parentID) {
                this.getObjektByID(parentID).subscribe({
                    next: async (res) => {
                        let currObjekt = res;

                        this.dbObjekte = await this.localStorageService.get('offlineObjekte', this.storageWebMain, false, []);

                        let existingObjekt: ObjektModels.ObjektElement = this.dbObjekte.filter((ch) => ch.objectID == parentID)[0];
                        const searchValues = this.treeViewCache.getObjectListData()?.find(obj => obj.objectID == currObjekt.objectID);
                        const obj = currObjekt;
                        obj['imageURL'] = obj['imageURL'] ?? searchValues?.objectTypeImg;
                        obj['projectLeader'] = searchValues?.projectLeader;
                        obj['name'] = searchValues?.name;
                        obj['objectTypeImg'] = searchValues?.objectTypeImg;
                        obj['objectTypeDesc'] = searchValues?.objectTypeDesc;
                        obj['depth'] = searchValues?.depth;
                        obj['parentID'] = obj['parentID'] ?? obj.parentObject;

                        if (existingObjekt) {
                            let index = this.dbObjekte.findIndex((x) => x.objectID == parentID);
                            this.dbObjekte[index] = obj;
                        } else {
                            this.dbObjekte.push(obj);
                        }

                        await this.localStorageService.add('offlineObjekte', this.dbObjekte, this.storageWebMain);

                        resolve(await this.saveParentObjectsInDB(currObjekt.parentObject));
                    }, error: () => resolve(false),
                });
            } else {
                resolve(true);
            }
        });
    }

    saveSubObjectsInDB(objectID: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.getSubObjektByID(objectID).subscribe({
                next: async (objekte) => {
                    if (objekte.length > 0) {
                        this.dbObjekte = await this.localStorageService.get('offlineObjekte', this.storageWebMain, false, []);

                        objekte.forEach((obj) => {
                            let existingObjekt: ObjektModels.ObjektElement = this.dbObjekte.filter((ch) => ch.objectID == obj.objectID)[0];

                            const searchValues = this.treeViewCache.getObjectListData()?.find(obj2 => obj2.objectID == obj.objectID);
                            obj['imageURL'] = obj['imageURL'] ?? searchValues?.objectTypeImg;
                            obj['projectLeader'] = searchValues?.projectLeader;
                            obj['name'] = searchValues?.name;
                            obj['objectTypeImg'] = searchValues?.objectTypeImg;
                            obj['objectTypeDesc'] = searchValues?.objectTypeDesc;
                            obj['depth'] = searchValues?.depth;
                            obj['parentID'] = obj['parentID'] ?? obj.parentObject;

                            if (existingObjekt) {
                                let index = this.dbObjekte.findIndex((x) => x.objectID == obj.objectID);
                                this.dbObjekte[index] = obj;
                            } else {
                                this.dbObjekte.push(obj);
                            }
                        });
                        await this.localStorageService.add('offlineObjekte', this.dbObjekte, this.storageWebMain);
                        resolve(true);
                    } else {
                        resolve(true);
                    }
                },
            });
        });
    }

    saveObjectAdressZuordInDB(objectID: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.getOfflineObjektAdressenZuordnungen(objectID).subscribe({
                next: async (objekteAdrZuord) => {
                    if (objekteAdrZuord.length > 0) {
                        this.dbObjektAdressZuord = await this.localStorageService.get('offlineObjekteAdressZuord', this.storageWebMain, false, []);

                        objekteAdrZuord.forEach((objAdrZuo) => {
                            const existingObjektAdrZuord = this.dbObjektAdressZuord.find((ch) => ch.id == objAdrZuo.id);
                            if (existingObjektAdrZuord) {
                                let index = this.dbObjektAdressZuord.findIndex((x) => x.objectID == objAdrZuo.id);
                                this.dbObjektAdressZuord[index] = objAdrZuo;
                            } else {
                                this.dbObjektAdressZuord.push(objAdrZuo);
                            }
                        });

                        await this.localStorageService.add('offlineObjekteAdressZuord', this.dbObjektAdressZuord, this.storageWebMain);
                        resolve(true);
                    } else {
                        resolve(true);
                    }
                },
            });
        });
    }

    /* Speichert die Adressen eines Objektes und dessen Unterobjekte in die DB */
    saveObjectAdressInDB(objectID: string): Promise<boolean> {
        return new Promise((resolve) => {
            this.getOfflineObjektAdressen(objectID).subscribe({
                next: async (objekteAdressen) => {
                    if (objekteAdressen.length > 0) {
                        this.dbObjektAdressen = await this.localStorageService.get('offlineObjekteAdressen', this.storageWebMain, false, []);
                        objekteAdressen.forEach((objAdr) => {
                            let existingObjektAdr: ObjektModels.ObjektAdresseShort = this.dbObjektAdressen.filter((a) => a.adressID == objAdr.adressID)[0];
                            if (existingObjektAdr) {
                                let index = this.dbObjektAdressen.findIndex((x) => x.adressID == objAdr.adressID);
                                this.dbObjektAdressen[index] = objAdr;
                            } else {
                                this.dbObjektAdressen.push(objAdr);
                            }
                        });

                        await this.localStorageService.add('offlineObjekteAdressen', this.dbObjektAdressen, this.storageWebMain);
                        resolve(true);
                    } else {
                        resolve(true);
                    }
                },
            });
        });
    }

    // Speichert in der IndexexDB, von wann der Offline-Abzug war
    public saveOfflineState(objectID: string): Promise<ObjektModels.ObjektOfflineStatus | undefined> {
        return new Promise(async (resolve) => {
            if (objectID) {
                let objStatus: ObjektModels.ObjektOfflineStatus = {
                    offlineID: uuidv4(), objectID: objectID, abzugDatum: new Date(),
                };

                this.dbObjektOfflineStates = await this.localStorageService.get('offlineObjektStatus', this.storageWebMain, false, []);
                this.dbObjektOfflineStates.push(objStatus);
                await this.localStorageService.add('offlineObjektStatus', this.dbObjektOfflineStates, this.storageWebMain);
                resolve(objStatus);
            } else {
                resolve(undefined);
            }
        });
    }

    public reloadObjActivities() {
        if (this.reloadObjActivitiesFunc) {
            this.reloadObjActivitiesFunc();
        }
    }

    public checkObjektZuordTypeUnique(dataObjektAdresseZuord: ObjektModels.ObjektAdresseZuord): Promise<boolean> {
        return new Promise((resolve) => {
            this.getObjektAdressenZuordnungen(dataObjektAdresseZuord.objectID).subscribe({
                next: (objAdrZuordnungen) => {
                    if (objAdrZuordnungen && objAdrZuordnungen.length > 0) {
                        let zuordnung = objAdrZuordnungen.filter((oaz) => oaz.addressType === 1);
                        if (zuordnung && zuordnung.length === 1) {
                            resolve(false); // Es existiert schon eine Kundenadresse
                        } else {
                            resolve(true); // Es existiert noch keine Kundenadresse
                        }
                    } else {
                        resolve(true); // Es existieren keine Objektadressen
                    }
                }, error: (err) => console.error('Prüfung der Objektadress-Zuordnungen verursacht ein Fehler: ', err),
            });
        });
    }

    public addObjectAddress(dataObjektAdresseZuord: ObjektModels.ObjektAdresseZuord) {
        let url = this.webServiceConfig!.objUrl;
        return this.http.post<ObjektModels.ObjektAdresseZuord>(url + '/UploadObjektAdressZuord', dataObjektAdresseZuord, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    public getAdressViewContent(objektAdresseZuord: ObjektModels.ObjektAdresseZuord): Observable<string> {
        let headers = new HttpHeaders({
            'Content-Type': 'text/plain; charset=utf-8', Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        let url = this.webServiceConfig!.objUrl;
        if (objektAdresseZuord.contactID) {
            return this.http.get<string>(url + '/GetObjektAddressView?AdressID=' + objektAdresseZuord.adressID + '&ContactID=' + objektAdresseZuord.contactID, {headers, responseType: 'text' as 'json'});
        } else {
            return this.http.get<string>(url + '/GetObjektAddressView?AdressID=' + objektAdresseZuord.adressID, {headers, responseType: 'text' as 'json'});
        }
    }

    copyObjectAddressTopDown(id: string) {
        const formData = new FormData();
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });
        const params: HttpParams = new HttpParams().set('id', id);

        const options = {
            headers: headers, params: params,
        };
        let url = this.webServiceConfig!.objUrl;
        return this.http.post<Response>(url + '/CopyObjektAdressZuord', formData, options);
    }

    public delObjectAddress(id: string) {
        const formData = new FormData();
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });
        const params: HttpParams = new HttpParams().set('id', id);

        const options = {
            headers: headers, params: params,
        };
        let url = this.webServiceConfig!.objUrl;
        return this.http.post<Response>(url + '/DeleteObjektAdressZuord', formData, options);
    }

    public deleteObjektAktivitaet(objectID: string, id: string, art: number) {
        const formData = new FormData();
        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });
        const params: HttpParams = new HttpParams()
            .set('ObjectID', objectID)
            .set('ID', id)
            .set('Art', art);

        const options = {
            headers: headers, params: params,
        };
        let url = this.webServiceConfig!.objUrl;
        return this.http.post<Response>(url + '/DeleteObjektAktivitaet', formData, options);
    }

    checkObjectDeleteRequest(objectID: string): Observable<ObjektModels.ObjektReferenzInfos> {
        let url = this.webServiceConfig!.objUrl;
        return this.http.get<ObjektModels.ObjektReferenzInfos>(url + '/GetDelObjektInfos?ObjectID=' + objectID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    /* Start Objektsuche nach mehreren Kriterien */

    public getObjektSucheDaten(): Observable<ObjektModels.ObjektSucheDaten> {
        let url = this.webServiceConfig!.objUrl;
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        return this.http.get<ObjektModels.ObjektSucheDaten>(url + '/GetObjektSucheDaten?mandator=' + user.mandatorID, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).pipe(
            tap(data => {
                this.localStorageService.add('offlineSearchData', data, 'SwissWebMainDB', false, true);
            }),
            catchError(async (err, caught) => {
                return await this.localStorageService.get('offlineSearchData', 'SwissWebMainDB', true, {objektStatus: [], objektOrte: [], projektleiter: []});
            })
        );
    }

    /* Ende Objektsuche nach mehreren Kriterien */
    createObjectDialog(dialogData: any): Promise<ObjektModels.ObjektElement | boolean> {
        return new Promise((resolve) => {
            if (dialogData.parentObjectID == null) {
                dialogData.parentObjectID = this.currentObjectID;
            }

            this.dialog.open(ObjektDialogComponent, {disableClose: true, data: dialogData, panelClass: 'swissdefault-dialog-container', closeOnNavigation: true}).afterClosed().subscribe({
                next: (result) => {
                    if (result !== 'canceled') {
                        const objDialogResult = (result as ObjektModels.ObjektDialogResult);
                        this.newObjekt(objDialogResult.objektElement).subscribe({
                            next: () => {
                                if (objDialogResult.objektAdresseZuord) {
                                    objDialogResult.objektAdresseZuord.objectID = objDialogResult.objektElement.objectID;
                                    this.addObjectAddress(objDialogResult.objektAdresseZuord).subscribe({
                                        next: () => {
                                            resolve(objDialogResult.objektElement);
                                        }, error: () => resolve(false)
                                    });
                                } else if (objDialogResult.swissFields.filter((sf) => sf.objektAdressZuordnungen !== null && sf.objektAdressZuordnungen !== undefined).length > 0) {
                                    this.saveObjektAdressZuordnungen(objDialogResult).then((res) => {
                                        if (res) {
                                            resolve(objDialogResult.objektElement);
                                        }
                                    });
                                } else {
                                    resolve(objDialogResult.objektElement);
                                }
                            }, error: () => resolve(false)
                        });
                    } else {
                        resolve(false);
                    }
                },
            });
        });
    }

    public showSaveOKMessage() {
        this.svcMessageBox.showSuccess('Das Objekt wurde erfolgreich erstellt.', 'Erfolgreich');
    }

    public showSaveErrorMessage() {
        this.svcMessageBox.showErrorOK('MAIN_APP_DIALOG.ERROR_DIALOG.OBJECT_CREATION_ERROR.text', 'MAIN_APP_DIALOG.ERROR_DIALOG.OBJECT_CREATION_ERROR.title');
    }

    async loadRapportLogo(): Promise<String> {
        if (!this.rapportLogoLoaded) {
            await this.loadRapportLogoFile();
            await this.loadRapportLogoString();
            this.rapportLogoLoaded = true;
        }

        return new Promise((resolve) => {
            if (this.rapportLogoLoaded) {
                return resolve(this.logoBase64Str);
            }
        });
    }


    async saveObjektRapport(objectID: string, fileName: string, binaryPDF: Blob): Promise<boolean> {
        const user = this.userAuthService.getCurrentAuthenticatedUser()!;
        let saRappFile: SwissInternModels.BaseFile = {
            documentID: uuidv4(), description: 'Kundenbesuch', createdBy: user.firstName + ' ' + user.lastName, createdDate: new Date(), objectID: objectID, fileName: fileName, binary: binaryPDF, uploaded: false,
        };

        await this.localStorageService
            .get('rapporte', this.dbStorageSI)
            .then((value) => {
                if (value === false) {
                    // Es sind keine Rapporte in der DB vorhanden
                    this.dbRapporte = [];
                } // Es sind schon Rapporte in der DB vorhanden
                else {
                    this.dbRapporte = <SwissInternModels.BaseFile[]>value;
                }
            });
        return new Promise((resolve) => {
            try {
                this.dbRapporte.push(saRappFile);
                this.localStorageService.add('rapporte', this.dbRapporte, this.dbStorageSI);
                this.uploadRapport(this.blobToFile(binaryPDF, fileName), objectID, saRappFile.documentID, fileName).subscribe({
                    next: () => {
                        this.dbRapporte.filter((x) => x.documentID === saRappFile.documentID)[0].uploaded = true;
                        this.localStorageService.add('rapporte', this.dbRapporte, this.dbStorageSI);
                    }, error: (err) => console.error('Fehler beim Hochladen eines Objekt-Rapports', err), complete: () => {
                        resolve(true);
                    },
                });
            } catch {
                resolve(false);
            }
        });
    }

    uploadRapport(file: File, objectID: string, documentID: string, filename: string) {
        const formData = new FormData();

        const headers = new HttpHeaders({
            //'Content-Type':  'multipart/form-data', --> Darf nicht vorhanden sein!!
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo),
        });

        const params: HttpParams = new HttpParams()
            .set('objectID', objectID)
            .set('documentID', documentID)
            .set('fileName', filename);

        const options = {
            headers: headers, params: params,
        };

        formData.append('file', file); // Der 1. Parameter 'file' muss übereinstimmen mit dem Parameter im Endpunkt
        let url = this.webServiceConfig!.objUrl;
        return this.http.post<Response>(url + '/UploadRapport', formData, options);
    }

    public async loadObjektRapportLogo() {
        if (this.swissAppReports === undefined) {
            this.swissAppReports = await this.svcSwissSettings.getServiceAppReports();
        }

        if (this.swissAppReports) {
            let objektReport: SwissAppConfigModels.SwissAppReport | undefined;
            objektReport = this.swissAppReports.filter(x => x.iD_SwissAppReportType === 2)[0];

            if (objektReport && objektReport.pdfReportHeaderLogo) {
                this
                    .getRapportHeaderLogo(objektReport.pdfReportHeaderLogo)
                    .subscribe({
                        next: (res: any) => {
                            this.doLoadRapportHeaderLogo = false;
                            this.localStorageService
                                .add('rapport_header_logo', res, this.dbStorageSI)
                                .then((res) => {
                                    if (res == 'success') {
                                    } else {
                                        console.error('Rapport-Header-Logo nicht in Indexed-DB gespeichert');
                                    }
                                }, () => console.error('Reject: Rapport-Header-Logo nicht in Indexed-DB gespeichert'));
                        }, error: (error: any) => console.error('Rapport-Header-Logo nicht heruntergeladen: ', error),
                    });
            } else {
                // SwissAppConfig ist noch nicht geladen
                this.doLoadRapportHeaderLogo = true;
            }
        }
    }

    makeRapport(si_PDFRapport: SwissInternModels.SI_PDFRapport, objectID: string, totalPreisExkl: number, totalMwSt: number, totalPreisInkl: number, signatureCanvas: ElementRef<HTMLCanvasElement>, isSigned: boolean): Promise<boolean> {
        return new Promise((resolve) => {
            if (si_PDFRapport.invoice) {
                this.svcPDFReport.AddFontArial(si_PDFRapport.invoice.doc);
                this.svcPDFReport.AddFontHelveticaBold(si_PDFRapport.invoice.doc);
                console.log('Font-List', si_PDFRapport.invoice.doc.getFontList());

                if (si_PDFRapport.invoice.template.SA_InvoiceItems) {
                    let obj: ObjektModels.ObjektElement;
                    this.getObjektByID(objectID).subscribe({
                        next: (res: ObjektModels.ObjektElement) => {
                            if (res) {
                                obj = res;

                                this.getClientAddress(objectID, GlobalModels.AdressTypes.Kunde).then((res) => {

                                    this.svcPDFReport.generateSIRapport(si_PDFRapport, objectID, totalPreisExkl, totalMwSt, totalPreisInkl, signatureCanvas!, isSigned, res, obj)
                                        .then((res) => {
                                            if (res) {
                                                let reportResult = <ReportModels.BaseReportResult>res;
                                                this.saveObjektRapport(reportResult.objectID, reportResult.name, reportResult.binary).then((res) => {
                                                    if (res) {
                                                        resolve(true);
                                                        this.svcMessageBox.showSuccess('Der Objekt-Rapport wurde erfolgreich erstellt.', 'Erfolgreich');
                                                    }
                                                }, (err) => {
                                                    console.error('Der Objekt-Rapport konnte nicht gespeichert werden!!', err);
                                                    this.svcMessageBox.showError('Der Objekt-Rapport konnte nicht gespeichert werden!!', '!! FEHLER !!');
                                                    resolve(false);
                                                });
                                            }
                                            resolve(true);
                                        });
                                });
                            }
                        }, error: (error: string) => (console.error('Fehler bei der Objektermittlung => makeRapport(). Fehler: ', error))
                    });
                }
            }
        });
    }

    loadRapportLogoFromIndexedDB(): Promise<Blob> {
        return new Promise((resolve) => {
            this.localStorageService
                .get('rapport_header_logo', this.dbStorageSI)
                .then((value) => {
                    if (value !== false) {
                        resolve(<Blob>value);
                    } else {
                        throw new Error('In der IndexedDB ist kein Rapport-Header-Logo vorhanden!');
                    }
                });
        });
    }

    /* START: Logo für Objekt-Rapport */

    async loadRapportLogoFile() {
        this.logoBlob = await this.loadRapportLogoFromIndexedDB();
    }

    loadRapportLogoFromBlob(): Promise<string> {
        return new Promise((resolve) => {
            var reader = new FileReader();
            reader.readAsDataURL(this.logoBlob!);
            reader.onloadend = () => {
                resolve(String(reader.result));
            };
        });
    }

    async loadRapportLogoString() {
        this.logoBase64Str = await this.loadRapportLogoFromBlob();
    }

    public getRapporteByObjectID(objectID: string) {
        if (this.dbRapporte) {
            return this.dbRapporte.filter((x) => x.objectID === objectID);
        } else {
            return [];
        }
    }

    /* Speichert in der indexed-DB eine neue Kopie einer PDF-Vorlage für den ausgewählten Auftrag */
    async saveObjektReport(objectID: string): Promise<ServiceAppModels.SA_ChecklistUploadElement | undefined> {
        const fileName = 'Messereport.pdf';

        if (this.currObjektReportTemplate) {
            let objReportFile: SwissInternModels.BaseFile = {
                documentID: uuidv4(),
                description: 'Messereport',
                createdBy: this.userAuthService.getCurrentAuthenticatedUser()!.firstName + ' ' + this.userAuthService.getCurrentAuthenticatedUser()!.lastName,
                createdDate: new Date(),
                objectID: objectID,
                fileName: fileName,
                binary: this.currObjektReportTemplate.pdfFileTemplate,
                uploaded: false
            };

            const value = await this.localStorageService.get('SwissAppObjektReporte', this.storageWebMain);
            if (value === false) {
                // Es sind keine Rapporte in der DB vorhanden
                this.dbObjektReporte = [];
            } // Es sind schon Rapporte in der DB vorhanden
            else {
                this.dbObjektReporte = <SwissInternModels.BaseFile[]>value;
            }

            /* Messereport-Namen pro Objekt eindeutig machen, damit mehrere Messenreporte gespeichert werden können */
            if (this.dbObjektReporte.length > 0) {
                if (this.dbObjektReporte.filter((chlst) => chlst.objectID === objectID).length > 0) {
                    objReportFile.fileName = this.getUniqueObjektReportFileName(objectID);
                }
            }

            this.dbObjektReporte.push(objReportFile);
            this.localStorageService.add('SwissAppObjektReporte', this.dbObjektReporte, this.storageWebMain);

            this.objReportUploadElement = {
                documentID: objReportFile.documentID, objectID: objectID, binary: this.currObjektReportTemplate!.pdfFileTemplate, fileName: objReportFile.fileName,
            };

            return this.objReportUploadElement;
        } else {
            return undefined;
        }
    }

    public getRapporteByDocumentID(documentID: string): SwissInternModels.BaseFile {
        return this.dbRapporte.filter((x) => x.documentID === documentID)[0];
    }

    public checkObjectPW(ob: ObjektModels.ObjektElement): Promise<boolean> {
        return new Promise((resolve) => {
            this.needsPassword(ob).then(obj => {
                if (obj.password) {
                    this.localStorageService.get('unlockedObjects', this.storageWebConfig).then((unlockedObjects: any) => {
                        if (!unlockedObjects) {
                            unlockedObjects = [];
                        }
                        if (unlockedObjects.includes(obj.objectID)) {
                            //pw had been entered before
                            resolve(true);
                        } else {
                            this.openObjektSchutzDialog(obj).then((res) => {
                                if (res) {
                                    unlockedObjects.push(obj.objectID);
                                    this.localStorageService.add('unlockedObjects', unlockedObjects, this.storageWebConfig);
                                    resolve(res);
                                } else {
                                    resolve(res);
                                }
                                // Res ist true, wenn das Passwort korrekt ist
                            });
                        }
                    });
                } else {
                    resolve(true);
                } // Kein Passwort vorhanden
            });
        });
    }

    /* ENDE: Logo für Objekt-Rapport */
    needsPassword(obj: ObjektModels.ObjektElement): Promise<ObjektModels.ObjektElement> {
        return new Promise((resolve) => {
            if (obj.password) {
                resolve(obj);
            } else if (obj.parentObject) {
                if (this.helperService.isWebServiceAvailable()) {
                    //removed for performance
                    /*this.getObjektByID(obj.parentObject, false).subscribe(p => {
                        this.needsPassword(p).then(obj => resolve(obj));
                    });*/
                    resolve(obj);
                } else {
                    resolve(obj);
                }
            } else {
                resolve(obj);
            }
        });

    }

    openObjektSchutzDialog(dialogData: ObjektModels.ObjektElement): Promise<boolean> {
        return new Promise((resolve) => {
            this.dialog.open(ObjektSchutzComponent, {
                disableClose: true, width: '400px', data: dialogData, panelClass: 'swissdefault-dialog-container',
            })
                .afterClosed()
                .subscribe({
                    next: (result) => {
                        if (result) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    },
                });
        });
    }

    async loadObjektReporte() {
        this.localStorageService
            .get('SwissAppObjektReporte', this.storageWebMain)
            .then((value) => {
                if (value === false) {
                    // Es sind keine Rapporte in der DB vorhanden
                    this.dbObjektReporte = [];
                } // Es sind schon Rapporte in der DB vorhanden
                else {
                    this.dbObjektReporte = <SwissInternModels.BaseFile[]>value;
                }
            });
    }

    addObjektReportInLocalDB(documentID: string, objectID: string, desciption: string, fileName: string, binaryPDF: Blob): Promise<boolean> {
        return new Promise((resolve) => {
            let objRepFile: SwissInternModels.BaseFile = {
                documentID: documentID, description: desciption, createdBy: this.userAuthService.getCurrentAuthenticatedUser()!.loginname, createdDate: new Date(), objectID: objectID, fileName: fileName, binary: binaryPDF, uploaded: true
            };
            this.dbObjektReporte.push(objRepFile);
            this.localStorageService.add('SwissAppObjektReporte', this.dbObjektReporte, this.storageWebMain);
            resolve(true);
        });
    }

    public async replaceObjektReportInIndexedDB(documentID: string, binary: Blob) {
        await this.localStorageService.get('SwissAppObjektReporte', this.storageWebMain).then((value) => {
            if (value === false) {
                // Es sind keine Checklisten in der DB vorhanden
                this.dbObjektReporte = [];
            } // Es sind schon Checklisten in der DB vorhanden
            else {
                this.dbObjektReporte = <SwissInternModels.BaseFile[]>value;
                let objReportFile: SwissInternModels.BaseFile = this.dbObjektReporte.filter((ch) => ch.documentID === documentID)[0];
                if (objReportFile) {
                    let index = this.dbObjektReporte.findIndex((x) => x.documentID === documentID);
                    objReportFile.binary = binary;
                    this.dbObjektReporte[index] = objReportFile;
                    this.localStorageService.add('SwissAppObjektReporte', this.dbObjektReporte, this.storageWebMain);
                }
            }
        });
    }

    async getObjektReportExitsInLocalDB(documentID: string): Promise<BaseFile | undefined> {
        const value = await this.localStorageService.get('SwissAppObjektReporte', this.storageWebMain);
        return value.find((x) => x.documentID === documentID);
    }

    delLocalObjektReport(documentID: string) {
        // Wenn der Objekt-Report in der Indexed-DB vorhanden ist: löschen
        this.getObjektReportExitsInLocalDB(documentID).then((value) => {
            // Objekt-Report existiert im lokalen Speicher
            if (value) {
                let index = this.dbObjektReporte.findIndex((x) => (x.documentID = documentID));
                this.dbObjektReporte.splice(index, 1);
                this.localStorageService.add('SwissAppObjektReporte', this.dbObjektReporte, this.storageWebMain);
            }
        });
    }

    getObjektReportByDocumentID(documentID: string): SwissInternModels.BaseFile {
        return this.dbObjektReporte.filter((x) => x.documentID === documentID)[0];
    }

    async isObjectAvailableOffline(objektElement: ObjektModels.ObjektElement): Promise<ObjektModels.ObjektElement> {
        const offlineObjects = await this.localStorageService.get('offlineObjekte', 'SwissWebMainDB', false, []);
        return offlineObjects.find(offObj => offObj.objectID == objektElement.objectID);
    }

    async getOfflineObjects(): Promise<ObjektModels.ObjectListViewItem[]> {
        const objs = await this.localStorageService.get('offlineObjekte', 'SwissWebMainDB', false, []);
        const objs2: ObjektModels.ObjectListViewItem[] = objs.flatMap(obj => {
                return {
                    objectNumber: obj.objectNumber,
                    objectType: obj.objectType,
                    objectID: obj.objectID,
                    objectNr: obj.objectNumber,
                    note4: obj.note4,
                    description: obj.description,
                    status: obj.status,
                    projectLeader: obj.projectLeader,
                    town: obj.town,
                    name: obj.name,
                    imageURL: obj.imageURL,
                    objectTypeDesc: obj.objectTypeDesc,
                    objectTypeImg: obj.objectTypeImg,
                    depth: obj.depth,
                };
            }
        );
        return objs2;
    }

    public getObjectRangeOptions(objectType: string): Observable<ObjectRangeOption[]> {
        if (!this.webServiceConfig) {
            throw new Error('Webservice-Konfiguration ist nicht vorhanden!');
        }
        const url = this.webServiceConfig.objUrl;
        return this.http.get<ObjectRangeOption[]>(url + '/GetObjectNumberRange?ObjectType=' + objectType, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo));
    }

    private saveObjektAdressZuordnungen(objektDialogResult: ObjektModels.ObjektDialogResult): Promise<boolean> {
        return new Promise((resolve) => {
            let hasError = false;
            let anzFields = objektDialogResult.swissFields.filter((sf) => sf.objektAdressZuordnungen !== null && sf.objektAdressZuordnungen !== undefined).length;
            let anzSaved = 0;
            objektDialogResult.swissFields
                .filter((sf) => sf.objektAdressZuordnungen !== null)
                .forEach((sf) => {
                    if (sf.objektAdressZuordnungen) {
                        sf.objektAdressZuordnungen.forEach((oaz) => {
                            oaz.objectID = objektDialogResult.objektElement.objectID; // Neue ObjektID der AdressZuordnung hinzufügen
                            this.addObjectAddress(oaz).subscribe({
                                next: () => {
                                    anzSaved = anzSaved + 1;
                                    if (anzSaved === anzFields) {
                                        resolve(true);
                                    }
                                }, error: () => {
                                    hasError = true;
                                    resolve(false);
                                },
                            });
                        });
                    } else {
                        resolve(true);
                    }
                });
        });
    }

    private blobToFile = (theBlob: Blob, fileName: string): File => {
        var b: any = theBlob;
        //A Blob() is almost a File() - it's just missing the two properties below which we will add
        // TODO 2025-12-09 https://swissjira.atlassian.net/browse/EN-4215 LastModifiedDate is deprecated use last modified instead
        b.lastModifiedDate = new Date();
        b.name = fileName;

        //Cast to a File() type
        return <File>theBlob;
    };

    private getRapportHeaderLogo(filePath: string): Observable<any> {
        let url = this.webServiceConfig!.saUrl;

        const headers = new HttpHeaders({
            Authorization: this.helperService.getSwissAuthorization(this.userAuthService.currentUserInfo)
        });

        return this.http.get<any>(url + '/DownloadRapportLogo?isHeader=true&filePath=' + filePath, {headers: headers, responseType: 'blob' as 'json'});
    }

    private getUniqueObjektReportFileName(objectID: string): string {
        let praefix = 'Messereport';
        let fileExtension = '.pdf';
        let currFileName = praefix + fileExtension;
        let incValue = 0;
        while (this.dbObjektReporte.filter((c) => c.objectID === objectID && c.fileName === currFileName).length > 0) {
            incValue += 1;
            currFileName = praefix + '_' + incValue + fileExtension;
        }
        return currFileName;
    }
}
